import React, { useRef, useState } from 'react';
import PartialCTA from '../partials/cta';
import { ComingSoon } from '.';

interface TabsProps {
  tabsData: {
    theme: string;
    tab_list: TabItem[];
  };
}

const TabType = {
  Simple: 'simple_tab_item',
  Detailed: 'detailed_tab_item',
  BTab: 'btab_item',
} as const;

const TabsTop: React.FC<TabsProps> = ({ tabsData }) => {
  const [active, setActive] = useState(0);
  const tabsRef = useRef<HTMLUListElement>(null);
  const snailRef = useRef<HTMLLIElement>(null);
  const theme = `tabs-header--${tabsData.theme || 'light'}`;

  const toggleTabs = (index: number) => {
    if (index !== active) {
      moveSnail(index);
    }
  };

  const moveSnail = (index: number) => {
    if (!snailRef.current || !tabsRef.current) {
      return;
    }

    const activeTab = tabsRef.current.children[index + 1] as HTMLElement;

    if (!activeTab) {
      return;
    }

    // get the left or right direction for the snail to move
    const direction = index > active ? 'right' : 'left';

    const activeTabRect = activeTab.getBoundingClientRect();
    const tabsRect = tabsRef.current.getBoundingClientRect();
    const getTabsPadding = () => {
      if (!tabsRef.current) {
        return 0;
      }
      const tabsStyle = window.getComputedStyle(tabsRef.current);
      return parseInt(tabsStyle.paddingLeft);
    };
    const tabsPadding = getTabsPadding();
    const snailLeft = activeTabRect.left - tabsRect.left - tabsPadding;

    // if left, move and grow snail to the left
    if (direction === 'left') {
      snailRef.current.style.left = `${snailLeft + tabsPadding}px`;
      snailRef.current.style.width = `${activeTabRect.width}px`;
    }
    if (direction === 'right') {
      snailRef.current.style.width = `${activeTabRect.width}px`;
    }

    // after 100 ms, shrink the snail back to the active tab width
    setTimeout(() => {
      if (!snailRef.current || !tabsRef.current) {
        return;
      }
      if (direction === 'right') {
        snailRef.current.style.left = `${snailLeft + tabsPadding}px`;
      }
      snailRef.current.style.width = `${activeTabRect.width}px`;
      setActive(index);
    }, 100);
  };

  return (
    <>
      <ul
        ref={tabsRef}
        className={`tabs-header ${theme}`}
      >
        <li
          ref={snailRef}
          className='tabs-header__snail'
        />
        {tabsData.tab_list.map((tab, index) => {
          return (
            <li
              key={index}
              className={`tab-title ${active === index ? 'is-active' : ''}`}
              onClick={() => toggleTabs(index)}
            >
              {tab.tab_icon && (
                <img
                  src={tab.tab_icon}
                  alt={tab.tab_title}
                  title={tab.tab_title}
                  loading='lazy'
                />
              )}
              {tab.tab_title && tab.tab_title}
              {tab.style && (
                <ComingSoon
                  style={tab.style}
                  override='tabs'
                />
              )}
            </li>
          );
        })}
      </ul>

      <div className='tabs-wr'>
        {tabsData.tab_list.map((tab, index) => {
          if (tab._template === TabType.Detailed) {
            return (
              <DetailedTabItem
                key={index}
                tab={tab as DetailedTabItem}
                isActive={active === index}
              />
            );
          } else if (tab._template === TabType.Simple) {
            return (
              <SimpleTabItem
                key={index}
                tab={tab as SimpleTabItem}
                isActive={active === index}
                lastChild={index === tabsData.tab_list.length - 1}
              />
            );
          }
        })}
      </div>
    </>
  );
};

const SimpleTabItem: React.FC<{
  tab: SimpleTabItem;
  isActive: boolean;
  lastChild: boolean;
}> = ({ tab, isActive, lastChild }) => {
  const fontSize = tab.font_size ? tab.font_size : '';
  const activeClass = isActive ? 'is-active' : '';
  const bgClass = tab.show_background ? 'has-background' : '';

  return (
    <div
      className={`block-tab__content simple_tab__content ${lastChild ? 'last_child' : ''} ${activeClass} ${bgClass}`}
    >
      {tab.html && (
        <div
          key={tab.tab_title + Math.random()}
          className='relative w-full overflow-hidden pt-[56.25%]'
        >
          <div
            className='absolute left-0 top-0 h-full w-full'
            dangerouslySetInnerHTML={{
              __html: tab.html,
            }}
          ></div>
        </div>
      )}
      {tab.image && !tab.html && (
        <img
          className='content-image'
          src={tab.image}
          alt={tab.image_alt_text}
          title={tab.image_alt_text}
          loading='lazy'
        />
      )}
      {tab.text && (
        <div
          className='text-formatted'
          style={fontSize ? { fontSize: `${fontSize}rem` } : {}}
          dangerouslySetInnerHTML={{ __html: tab.text }}
        ></div>
      )}
    </div>
  );
};

const DetailedTabItem: React.FC<{
  tab: DetailedTabItem;
  isActive: boolean;
}> = ({ tab, isActive }) => {
  return (
    <li className={`tab-content ${isActive ? 'is-active' : ''}`}>
      <div className='row'>
        <div className='col-lg-5 col-md-12 offset-lg-1 text is-desktop'>
          {tab.title && (
            <h4 dangerouslySetInnerHTML={{ __html: tab.title }}></h4>
          )}
          {tab.text && (
            <div
              className='text-formatted'
              dangerouslySetInnerHTML={{ __html: tab.text }}
            ></div>
          )}
          {tab.cta_field && <PartialCTA data={tab.cta_field} />}
        </div>
        <div className='col-lg-4 col-md-12 offset-lg-1 image is-desktop'>
          {tab.image && (
            <img
              src={tab.image}
              alt={tab.image_alt_text}
              title={tab.image_alt_text}
              loading='lazy'
            />
          )}
        </div>
        <div className='col-md-12 is-mobile'>
          <ComingSoon style={tab.style} />
          {tab.tab_title && <h3>{tab.tab_title}</h3>}
          {tab.title && (
            <h4 dangerouslySetInnerHTML={{ __html: tab.title }}></h4>
          )}
          {tab.image && (
            <img
              src={tab.image}
              alt={tab.image_alt_text}
              title={tab.image_alt_text}
              loading='lazy'
            />
          )}
          {tab.text && (
            <div
              className='text-formatted'
              dangerouslySetInnerHTML={{ __html: tab.text }}
            ></div>
          )}
          {tab.cta_field && <PartialCTA data={tab.cta_field} />}
        </div>
      </div>
    </li>
  );
};

export default TabsTop;
